import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Bell, CheckCircle2, Clock, MessageSquare, Search, X } from 'lucide-react';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import BBALoader from '../components/BBALoader';
import PortalBackground from '../components/PortalBackground';
import CrmStatCard from '../components/crm/CrmStatCard';
import { FIELD_CLASS, LABEL_CLASS, TH_CLASS, FilterSelect } from '../components/crm/CrmFilterUi';
import { getAllFires, createFire } from '../services/firebase/fires';
import { getInternalUsers } from '../services/userService';
import { getAccountContacts } from '../services/firebase/users';
import { getAccounts } from '../services/firestoreService';
import { useResponsive } from '../contexts/ResponsiveContext';
import { useAuth } from '../contexts/AuthContext';

/** Status filter: Active or Pending (excludes Resolved). */
const STATUS_ACTIVE_PENDING = '__active_pending__';

// Status pill palette (dot + text + hairline border on a tinted surface)
const STATUS_PILL = {
  Active: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800/60',
  Pending: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800/60',
  Resolved: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800/60',
};

function getResolvedCompanyName(fire, accountsList) {
  if (fire.isInternal) return 'Internal';
  const raw = (fire.companyName || fire.account || '').trim();
  if (raw === 'Internal') return 'Internal';
  if (fire.accountId) {
    const acc = accountsList.find((a) => a.id === fire.accountId);
    if (acc?.company) return acc.company;
  }
  if (raw) {
    if (accountsList.some((a) => a.company === raw)) return raw;
    const rawLower = raw.toLowerCase();
    const byEmail = accountsList.find((a) => {
      const em = (a.contact?.email || '').trim().toLowerCase();
      if (!em) return false;
      if (em === rawLower) return true;
      const local = em.split('@')[0];
      return local === rawLower;
    });
    if (byEmail?.company) return byEmail.company;
  }
  return raw || '—';
}

const FiresPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const openCreateFromState = location.state && location.state.openCreate ? true : false;
  const defaultCompanyFromState = (location.state && location.state.defaultCompanyName) || '';
  const { isMobile } = useResponsive();
  const { currentUser } = useAuth();
  const assigneeDefaultAppliedRef = useRef(false);
  
  // State
  const [fires, setFires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(STATUS_ACTIVE_PENDING);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(openCreateFromState);
  const [newFire, setNewFire] = useState({
    name: '',
    companyName: defaultCompanyFromState,
    description: '',
    assignedTo: [],
    status: 'Active',
    deadline: '',
  });
  const [internalUsers, setInternalUsers] = useState([]);
  const [accountContacts, setAccountContacts] = useState([]);
  const [availableAssignees, setAvailableAssignees] = useState([]);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [accountsCatalog, setAccountsCatalog] = useState([]);
  const [clientSearch, setClientSearch] = useState('');

  // Load fires and users on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [firesData, usersData, accountsData] = await Promise.all([
          getAllFires(),
          getInternalUsers(),
          getAccounts()
        ]);
        
        // If no internal users found, try to get users from the users collection as fallback
        if (usersData.length === 0) {
          try {
            const { getInternalUsers: getFallbackUsers } = await import('../services/firebase/users');
            const fallbackUsers = await getFallbackUsers();
            setInternalUsers(fallbackUsers);
          } catch (fallbackError) {
            console.error('Fallback also failed:', fallbackError);
            setInternalUsers([]);
          }
        } else {
          setInternalUsers(usersData);
        }
        
        // Set client accounts (filter out internal accounts and get unique company names)
        const uniqueCompanies = [...new Set(accountsData.map(account => account.company).filter(Boolean))];
        setClientAccounts(uniqueCompanies);
        setAccountsCatalog(accountsData);
        
        setFires(firesData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // If we opened via navigation state, clear it so back/forward doesn't keep reopening
  useEffect(() => {
    if (openCreateFromState) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load account contacts when account is selected
  useEffect(() => {
    const loadAccountContacts = async () => {
      if (newFire.companyName && newFire.companyName !== 'Internal') {
        try {
          const contacts = await getAccountContacts(newFire.companyName);
          setAccountContacts(contacts);
        } catch (error) {
          console.error('Error loading account contacts:', error);
        }
      } else {
        setAccountContacts([]);
      }
    };

    loadAccountContacts();
  }, [newFire.companyName]);

  // Update available assignees when internal users or account contacts change
  useEffect(() => {
    const assignees = [
      ...internalUsers.map(user => ({
        id: user.id,
        name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown User',
        type: 'Internal'
      })),
      ...accountContacts.map(contact => ({
        id: contact.id,
        name: contact.name || contact.displayName || contact.email || 'Unknown Contact',
        type: 'Contact'
      }))
    ];
    setAvailableAssignees(assignees);
  }, [internalUsers, accountContacts]);

  useEffect(() => {
    if (!currentUser || assigneeDefaultAppliedRef.current) return;
    assigneeDefaultAppliedRef.current = true;
    const me =
      (currentUser.displayName && currentUser.displayName.trim()) ||
      currentUser.email ||
      '';
    if (me) setAssignedFilter(me);
  }, [currentUser]);

  // Calculate quick stats
  const quickStats = {
    internalFires: fires.filter(fire => fire.isInternal && fire.status === 'Active').length,
    clientFires: fires.filter(fire => !fire.isInternal && fire.status === 'Active').length,
    needsAttention: fires.filter(fire => fire.status === 'Active').length,
    pending: fires.filter(fire => fire.status === 'Pending').length,
    resolved: fires.filter(fire => fire.status === 'Resolved').length
  };

  const meLabel =
    (currentUser?.displayName && currentUser.displayName.trim()) ||
    currentUser?.email ||
    '';

  // Get unique values for filters (company = resolved CRM company name)
  const accounts = useMemo(
    () => [...new Set(fires.map((fire) => getResolvedCompanyName(fire, accountsCatalog)))],
    [fires, accountsCatalog]
  );
  const assignedToOptions = [
    ...new Set([
      ...(meLabel ? [meLabel] : []),
      ...fires.flatMap((fire) => (Array.isArray(fire.assignedTo) ? fire.assignedTo : [])),
    ]),
  ];
  const statusOptions = ["Active", "Pending", "Resolved"];

  // Filter client accounts based on search
  const filteredClientAccounts = clientAccounts.filter(company =>
    company.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const handleNewFireChange = (e) => {
    const { name, value } = e.target;
    setNewFire(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssigneeChange = (e) => {
    const { value, checked } = e.target;
    setNewFire(prev => ({
      ...prev,
      assignedTo: checked
        ? [...prev.assignedTo, value]
        : prev.assignedTo.filter(assignee => assignee !== value)
    }));
  };

  const handleCreateFire = async () => {
    try {
      // Validate required fields
      if (!newFire.name || !newFire.companyName) {
        alert('Please fill in all required fields');
        return;
      }

      const createdFire = await createFire({
        ...newFire,
        dateReported: new Date().toISOString(),
        isInternal: newFire.companyName === 'Internal'
      });

      // Update the fires list with the new fire
      setFires(prev => [createdFire, ...prev]);
      
      // Close modal and reset form
      setIsModalOpen(false);
      setNewFire({
        name: '',
        companyName: '',
        description: '',
        assignedTo: [],
        status: 'Active',
        deadline: '',
      });
    } catch (error) {
      console.error('Error creating fire:', error);
      alert('Failed to create fire. Please try again.');
    }
  };

  // Filter fires
  const filterFires = (fires) => {
    const q = search.toLowerCase();
    return fires.filter(fire => {
      const resolvedCompany = getResolvedCompanyName(fire, accountsCatalog);
      const assignedList = Array.isArray(fire.assignedTo) ? fire.assignedTo : [];
      const searchMatch = !search || 
        (fire.name?.toLowerCase() || '').includes(q) ||
        (fire.description?.toLowerCase() || '').includes(q) ||
        (fire.companyName?.toLowerCase() || '').includes(q) ||
        (resolvedCompany.toLowerCase() || '').includes(q) ||
        (assignedList.some(assignee => assignee.toLowerCase().includes(q)));
      
      const accountMatch = !accountFilter || resolvedCompany === accountFilter;
      const assignedMatch = !assignedFilter || assignedList.includes(assignedFilter);
      const statusMatch =
        !statusFilter ||
        (statusFilter === STATUS_ACTIVE_PENDING
          ? fire.status === 'Active' || fire.status === 'Pending'
          : fire.status === statusFilter);
      const dateMatch = (!startDate || fire.dateReported >= startDate) && 
                        (!endDate || fire.dateReported <= endDate);
      
      return searchMatch && accountMatch && assignedMatch && statusMatch && dateMatch;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 md:p-6 relative">
        <PortalBackground />
        <div className="relative flex items-center justify-center py-32">
          <BBALoader size={48} />
        </div>
      </div>
    );
  }

  const visibleFires = filterFires(fires);

  return (
    <div className={`min-h-screen ${isMobile ? 'p-3' : 'p-4 md:p-6'} relative`}>
      {/* Same ambient BBA background as the Leads, Clients and Tasks pages */}
      <PortalBackground />
      <div className="relative container mx-auto">
      {/* Header */}
      <div className={`flex items-center ${isMobile ? 'flex-col gap-3' : 'justify-between'} ${isMobile ? 'mb-4' : 'mb-6'}`}>
        <BackButton onClick={() => navigate('/crm')} />
        <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold tracking-tight text-gray-900 dark:text-gray-100`}>Fires</h1>
        <div className="w-10"></div> {/* Spacer to center the title */}
      </div>

      {/* Quick Stats Section */}
      <div className={`grid ${isMobile ? 'grid-cols-2 gap-2' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4'} ${isMobile ? 'mb-4' : 'mb-6'}`}>
        <CrmStatCard icon={AlertTriangle} label="Internal Fires" value={quickStats.internalFires} tone="red" compact={isMobile} />
        <CrmStatCard icon={MessageSquare} label="Client Fires" value={quickStats.clientFires} tone="orange" compact={isMobile} />
        <CrmStatCard icon={Bell} label="Needs Attention" value={quickStats.needsAttention} tone="yellow" compact={isMobile} />
        <CrmStatCard icon={Clock} label="Pending" value={quickStats.pending} tone="blue" compact={isMobile} />
        <CrmStatCard icon={CheckCircle2} label="Resolved" value={quickStats.resolved} tone="green" compact={isMobile} />
      </div>

      {/* Fires Section */}
      <div>
        {/* Filter Row */}
        <div className={`flex ${isMobile ? 'flex-col gap-2' : 'flex-wrap items-center gap-3'} mb-4`}>
          <div className={`relative ${isMobile ? 'w-full' : 'flex-1 min-w-[16rem]'}`}>
            <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              placeholder="Search fires by name, company, or assignee…"
              className={`${FIELD_CLASS} pl-10 pr-10 [&::-webkit-search-cancel-button]:hidden`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                title="Clear search"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <FilterSelect
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            className={isMobile ? 'w-full' : 'w-48'}
            aria-label="Company"
          >
            <option value="">All companies</option>
            {accounts.map((account, idx) => (
              <option key={idx} value={account}>{account}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={assignedFilter}
            onChange={(e) => setAssignedFilter(e.target.value)}
            className={isMobile ? 'w-full' : 'w-48'}
            aria-label="Assignee"
          >
            <option value="">All Assignees</option>
            {assignedToOptions.map((assignee, idx) => (
              <option key={idx} value={assignee}>{assignee}</option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={isMobile ? 'w-full' : 'w-44'}
            aria-label="Status"
          >
            <option value="">All Status</option>
            <option value={STATUS_ACTIVE_PENDING}>Active &amp; Pending</option>
            {statusOptions.map((status, idx) => (
              <option key={idx} value={status}>{status}</option>
            ))}
          </FilterSelect>
          <div className={`flex gap-2 ${isMobile ? 'w-full' : ''}`}>
            <input
              type="date"
              className={`${FIELD_CLASS} ${isMobile ? 'flex-1' : 'w-40'} px-3`}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              aria-label="Start date"
              title="Reported on or after"
            />
            <input
              type="date"
              className={`${FIELD_CLASS} ${isMobile ? 'flex-1' : 'w-40'} px-3`}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              aria-label="End date"
              title="Reported on or before"
            />
          </div>
          <div className={`flex ${isMobile ? 'justify-center' : 'ml-auto'}`}>
            <AddButton onClick={() => setIsModalOpen(true)} title="New fire" aria-label="New fire" />
          </div>
        </div>

        {/* Fires Table */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="relative flex items-center justify-center gap-2.5 px-5 py-4 border-b border-gray-200 dark:border-gray-800">
            <span className="text-base font-semibold text-gray-900 dark:text-white">
              {statusFilter === STATUS_ACTIVE_PENDING ? 'Active & Pending Fires' : statusFilter ? `${statusFilter} Fires` : 'All Fires'}
            </span>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              {visibleFires.length}
            </span>
          </div>
          <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-800">
              <tr>
                <th className={TH_CLASS}>Company</th>
                <th className={TH_CLASS}>Fire</th>
                <th className={`${TH_CLASS} hidden sm:table-cell`}>Date Reported</th>
                <th className={`${TH_CLASS} hidden md:table-cell`}>Assigned To</th>
                <th className={`${TH_CLASS} hidden lg:table-cell`}>Deadline</th>
                <th className={TH_CLASS}>Status</th>
              </tr>
            </thead>
            <tbody>
              {visibleFires.map(fire => (
                <tr
                  key={fire.id}
                  className="border-b border-gray-100 dark:border-gray-800 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer transition-colors"
                  onClick={() => navigate(`/crm/fires/${fire.id}`)}
                >
                  <td className="px-2 sm:px-4 py-3.5 text-sm text-gray-600 dark:text-gray-300 truncate max-w-24 sm:max-w-none">{getResolvedCompanyName(fire, accountsCatalog)}</td>
                  <td className="px-2 sm:px-4 py-3.5 text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-32 sm:max-w-none">{fire.name}</td>
                  <td className="px-2 sm:px-4 py-3.5 text-sm text-gray-600 dark:text-gray-300 hidden sm:table-cell">
                    {new Date(fire.dateReported).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                  </td>
                  <td className="px-2 sm:px-4 py-3.5 text-sm text-gray-600 dark:text-gray-300 hidden md:table-cell truncate max-w-32 lg:max-w-none">{(Array.isArray(fire.assignedTo) ? fire.assignedTo : []).join(', ')}</td>
                  <td className="px-2 sm:px-4 py-3.5 text-sm text-gray-600 dark:text-gray-300 hidden lg:table-cell">
                    {fire.deadline ? new Date(fire.deadline).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : '-'}
                  </td>
                  <td className="px-2 sm:px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_PILL[fire.status] || STATUS_PILL.Resolved}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" />
                      {fire.status}
                    </span>
                  </td>
                </tr>
              ))}
              {visibleFires.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-sm text-center text-gray-500 dark:text-gray-400">No fires match these filters</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>
      </div>

      {/* New Fire Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white text-center">Create New Fire</h2>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div>
                <label className={LABEL_CLASS}>Fire Name</label>
                <input
                  type="text"
                  name="name"
                  value={newFire.name}
                  onChange={handleNewFireChange}
                  className={`${FIELD_CLASS} px-3`}
                  placeholder="Enter fire name"
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>Description</label>
                <textarea
                  name="description"
                  value={newFire.description}
                  onChange={handleNewFireChange}
                  className={`${FIELD_CLASS} px-3`}
                  rows="3"
                  placeholder="Enter fire description"
                />
              </div>
              <div>
                <label className={LABEL_CLASS}>Account</label>
                <FilterSelect
                  name="companyName"
                  value={newFire.companyName}
                  onChange={handleNewFireChange}
                  className="w-full"
                  icon={false}
                >
                  <option value="">Select account</option>
                  <option value="Internal">Internal</option>
                </FilterSelect>

                {/* Client Search */}
                {newFire.companyName !== 'Internal' && (
                  <div className="mt-2">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search clients..."
                        className={`${FIELD_CLASS} pl-10 pr-10`}
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                      />
                      {clientSearch && (
                        <button
                          type="button"
                          onClick={() => setClientSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                          aria-label="Clear client search"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Client List */}
                    <div className="mt-2 max-h-32 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                      {filteredClientAccounts.length > 0 ? (
                        filteredClientAccounts.map((company, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setNewFire(prev => ({ ...prev, companyName: company }));
                              setClientSearch('');
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
                          >
                            {company}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                          {clientSearch ? 'No clients found' : 'No clients available'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className={LABEL_CLASS}>Assigned To</label>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-2 max-h-32 sm:max-h-48 overflow-y-auto bg-white dark:bg-gray-800">
                  {availableAssignees.length > 0 ? (
                    availableAssignees.map((assignee) => (
                      <div key={assignee.id} className="flex items-center gap-2 py-1 px-1">
                        <input
                          type="checkbox"
                          id={`assignee-${assignee.id}`}
                          value={assignee.name}
                          checked={newFire.assignedTo.includes(assignee.name)}
                          onChange={(e) => {
                            const { value, checked } = e.target;
                            setNewFire(prev => ({
                              ...prev,
                              assignedTo: checked
                                ? [...prev.assignedTo, value]
                                : prev.assignedTo.filter(name => name !== value)
                            }));
                          }}
                          className="h-4 w-4 text-red-600 border-gray-300 dark:border-gray-600 rounded focus:ring-red-500"
                        />
                        <label htmlFor={`assignee-${assignee.id}`} className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                          {assignee.name}
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({assignee.type})</span>
                        </label>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                      {loading ? 'Loading assignees...' : 'No assignees available'}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASS}>Status</label>
                  <FilterSelect
                    name="status"
                    value={newFire.status}
                    onChange={handleNewFireChange}
                    className="w-full"
                    icon={false}
                  >
                    {statusOptions.map((status, idx) => (
                      <option key={idx} value={status}>{status}</option>
                    ))}
                  </FilterSelect>
                </div>
                <div>
                  <label className={LABEL_CLASS}>Deadline</label>
                  <input
                    type="date"
                    name="deadline"
                    value={newFire.deadline}
                    onChange={handleNewFireChange}
                    className={`${FIELD_CLASS} px-3`}
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 flex justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateFire}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-xl shadow-sm hover:bg-red-700 transition-colors"
              >
                Create Fire
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiresPage;
